import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/lib/adminApi";
import { adminAuth } from "@/lib/adminAuth";

export default function AdminCandidates() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [quizId, setQuizId] = useState<string>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ id: string; name: string; cascade: boolean } | null>(null);

  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });
  const isSuperAdmin = me.data?.roles.includes("SuperAdmin") ?? false;

  const quizzes = useQuery({ queryKey: ["quizzes-picker"], queryFn: () => adminApi.listQuizzes({ pageSize: 200 }) });

  const list = useQuery({
    queryKey: ["candidates", search, quizId, from, to, page],
    queryFn: () => adminApi.listCandidates({
      search: search || undefined,
      quizId: quizId || undefined,
      registeredFrom: from ? new Date(from).toISOString() : undefined,
      registeredTo: to ? new Date(to + "T23:59:59").toISOString() : undefined,
      page, pageSize: 50,
    }),
  });

  const del = useMutation({
    mutationFn: (v: { id: string; cascade: boolean }) => adminApi.deleteCandidate(v.id, v.cascade),
    onSuccess: () => {
      setConfirm(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ["candidates"] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? "Delete failed."),
  });

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.totalCount / list.data.pageSize)) : 1;

  return (
    <main className="mx-auto max-w-6xl p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">Candidates</h1>
          <div className="text-sm text-brand-ink/60">
            {list.data ? `${list.data.totalCount} total` : "…"}
          </div>
        </div>
      </div>

      <div className="card mb-4 grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
        <input className="field-input" placeholder="Search name, email, mobile"
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <select className="field-input" value={quizId}
          onChange={(e) => { setQuizId(e.target.value); setPage(1); }}>
          <option value="">All quizzes</option>
          {quizzes.data?.items.map((q) => <option key={q.id} value={q.id}>{q.title}</option>)}
        </select>
        <div>
          <label className="text-xs text-brand-ink/60">Registered from</label>
          <input type="date" className="field-input"
            value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        </div>
        <div>
          <label className="text-xs text-brand-ink/60">Registered to</label>
          <input type="date" className="field-input"
            value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        </div>
        <div className="flex items-end">
          <button className="btn-secondary" onClick={() => { setSearch(""); setQuizId(""); setFrom(""); setTo(""); setPage(1); }}>
            Reset
          </button>
        </div>
      </div>

      {error && <div className="mb-3 text-sm text-brand">{error}</div>}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-light text-brand-ink">
              <tr>
                {["Name", "Email", "Mobile", "Category", "Registered", "Quizzes", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((c) => (
                <tr key={c.id} className="border-t border-brand-ink/10 align-top">
                  <td className="px-3 py-2">
                    <div>{c.firstName} {c.lastName}</div>
                    <div className="text-xs text-brand-ink/60">DOB {c.dateOfBirth} · Father {c.fatherName}</div>
                  </td>
                  <td className="px-3 py-2">{c.email}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.mobile}</td>
                  <td className="px-3 py-2">{c.category}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-brand-ink/70">
                    {new Date(c.registeredAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {c.quizzes.length === 0
                      ? <span className="text-brand-ink/50">No attempts</span>
                      : (
                        <ul className="space-y-0.5">
                          {c.quizzes.map((q) => (
                            <li key={q.quizId}>
                              <span className="font-medium">{q.quizTitle}</span>
                              <span className="ml-2 rounded-pill bg-brand-light px-2 py-0.5">{q.state}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-sm">
                    {c.attemptCount === 0 ? (
                      <button className="text-brand hover:underline"
                        onClick={() => setConfirm({ id: c.id, name: `${c.firstName} ${c.lastName}`, cascade: false })}>
                        Delete
                      </button>
                    ) : isSuperAdmin ? (
                      <button className="text-brand hover:underline"
                        onClick={() => setConfirm({ id: c.id, name: `${c.firstName} ${c.lastName}`, cascade: true })}>
                        Force delete
                      </button>
                    ) : (
                      <span className="text-brand-ink/40" title="Only SuperAdmin can delete candidates with attempt history">
                        —
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {list.data?.items.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-brand-ink/60">
                  No candidates match.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {list.data && list.data.totalCount > list.data.pageSize && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="text-brand-ink/60">Page {list.data.page} of {totalPages} · {list.data.totalCount} total</div>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-brand">
              {confirm.cascade ? "Force delete candidate?" : "Delete candidate?"}
            </h3>
            <p className="mt-2 text-sm text-brand-ink/80">
              {confirm.cascade ? (
                <>Purges <b>{confirm.name}</b> and all their attempts, camera snapshots, ID documents,
                and OTP records. This cannot be undone.</>
              ) : (
                <>Removes <b>{confirm.name}</b>. Only possible because they have no attempts on record.</>
              )}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="btn-primary bg-brand"
                onClick={() => del.mutate({ id: confirm.id, cascade: confirm.cascade })}
                disabled={del.isPending}>
                {del.isPending ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
